import { Either } from '@lib/monad';

import { fetchBrazeSdkAuthToken } from '@lib/api/BrazeService';

import { BrazeInstance, SdkAuthError, subscribeToSdkAuthErrors } from './braze';

/**
 * JSAD-7145. Owns the Braze SDK Authentication signature for the signed-in user.
 *
 * Lives outside React because two very different callers need it: the useBraze
 * identity effect, and useAuth's logout teardown.
 */

interface CachedSignature {
    userId: string;
    token: string;
    /** Unix seconds, straight from the backend's expires_at. */
    expiresAt: number;
}

// Treat a token as spent slightly before its real expiry so an in-flight Braze
// request cannot land on the far side of the boundary.
const EXPIRY_SKEW_SECONDS = 60;

let cached: CachedSignature | undefined;
let inflight: { userId: string; promise: Promise<string | undefined> } | undefined;
let errorHandlerInstalled = false;

const isUsable = (entry: CachedSignature | undefined, userId: string) =>
    !!entry &&
    entry.userId === userId &&
    entry.expiresAt - EXPIRY_SKEW_SECONDS > Math.floor(Date.now() / 1000);

const request = async (userId: string): Promise<string | undefined> => {
    const result = await fetchBrazeSdkAuthToken();

    if (!Either.isRight(result)) {
        return undefined;
    }

    const { token, expires_at: expiresAt } = Either.unwrap(result);
    cached = { userId, token, expiresAt };
    return token;
};

/**
 * Returns the signature to pass to Braze for `userId`, or undefined if one could not
 * be obtained. Undefined is a supported outcome: the caller still calls changeUser,
 * messaging degrades quietly, and ordering is unaffected.
 *
 * useBraze is mounted from ~17 components, so every user change fires this many times
 * over. Caching by user id plus a single in-flight promise collapses that to one
 * request. Keying on the user id is also what keeps one customer's token from ever
 * reaching another's session.
 */
export const getBrazeSignature = async (
    userId: string,
    { force = false }: { force?: boolean } = {}
): Promise<string | undefined> => {
    if (!force && isUsable(cached, userId)) {
        return cached?.token;
    }

    if (inflight && inflight.userId === userId && !force) {
        return inflight.promise;
    }

    const promise = request(userId).finally(() => {
        if (inflight?.promise === promise) {
            inflight = undefined;
        }
    });

    inflight = { userId, promise };
    return promise;
};

/**
 * Drops the cached signature. Called from useAuth's logout teardown so the next
 * customer to sign in on this device can never be handed the previous one's token.
 */
export const clearBrazeSignature = () => {
    cached = undefined;
    inflight = undefined;
};

/**
 * Registers the SDK Authentication failure callback exactly once.
 *
 * Braze retries rejected requests with exponential backoff until a valid JWT arrives,
 * so re-issuing here is the documented recovery path and the reason a token expiring
 * mid-session is a non-event. The idempotence guard matters because the caller is the
 * same effect that runs once per mounted useBraze consumer.
 */
export const installSdkAuthErrorHandler = (braze: BrazeInstance) => {
    if (errorHandlerInstalled) {
        return;
    }
    errorHandlerInstalled = true;

    subscribeToSdkAuthErrors(braze, async (error: SdkAuthError) => {
        const userId = error.userId ?? cached?.userId;
        if (!userId) {
            return;
        }

        const signature = await getBrazeSignature(userId, { force: true });
        if (!signature) {
            console.warn(
                `Braze SDK authentication failed (code ${error.errorCode}) and a replacement token could not be obtained.`
            );
            return;
        }

        braze.setSdkAuthenticationSignature(signature);
    });
};
